 import * as THREE from 'three';
 import { Sky } from './sky.js';
 import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
 import { parameters, app } from './core.js';
 import { createEnvironment, setLanternNight, setEnvironmentAutumn } from './environment.js';
 import { initInteraction } from './interaction.js';
 import { setupControls } from './ui-controls.js';
 import { Tree, setSnowStartsBare, MAX_MAIN_BRANCHES, MAX_GROW_BRANCHES } from './tree.js';
 import { updateFoliageWind } from './foliageShader.js';
 import { updateLeafFall } from './leafFall.js';
 import { updateWinter, initWinter } from './snow.js';
 import { createStarField } from './starfield.js';
 import { updateShootingStars } from './shootingStars.js';
 import { createAurora, updateAurora } from './aurora.js';
 import { createFireflies, updateFireflies } from './fireflies.js';
 import { createButterflies, updateButterflies } from './butterflies.js';
import { updateDeer } from './deer.js'; // wild deer roaming the distant meadow (built lazily when a ring is up)
import { updateShiba } from './shiba.js'; // a Shiba pup at the house (built lazily when the house is up)
 import './gallery.js'; // registers the gallery UI (window.saveBonsai, etc.)
import { notify } from './gallery.js'; // toast helper (reused for the main-branch cap message)
import { loadSharedTreeFromURL } from './shareBonsai.js'; // registers window.shareBonsai + inbound share-link loading
import { syncSceneToggles } from './settings.js'; // registers the settings drawer (window.toggleSettings) + scene-toggle sync
import './recorder.js'; // registers the clip recorder (window.recordBonsai)
import { playSfx } from './audio.js'; // SFX engine; grow/main-branch actions play through it
import './soundtrack.js'; // registers the ambient music player (window.nextTrack) + first-gesture fade-in
import { updateAmbient } from './ambient.js'; // time-of-day wildlife loops (birds/crickets), fed the live sun elevation below
import { initPineCones } from './pineCones.js'; // loads the pine-cone GLB; cones hang on pine/candle branches when toggled on

// Rocks by Quaternius [CC-BY] via Poly Pizza
// https://poly.pizza/u/Quaternius

    // Global variables. Pointer-interaction state now lives in interaction.js.
    let scene, camera, renderer, controls, tree;
    let sky, sun;
    let starField; // night star dome; opacity tracks the sun height (see animate)
    let aurora;    // northern-lights curtain; shows only on winter nights (see animate)
    let fireflies; // drifting fireflies; shows on green-season nights (see animate)
    let butterflies; // flitting butterflies; shows on clear summer days (see animate)
    let cloudsMoving = false; // Cloud Drift toggle: clouds are always drawn, this just animates them
    let cloudTime = 0;        // accumulated drift time (advances only while moving, so toggling on never jumps the clouds)
    let skyCycle = false;     // Sky preset "Cycle": continuously sweep the sun across the sky
    let cycleTime = 0;        // accumulated day-cycle time
    let skyTransition = null; // active tween to a static preset ({ t, from, to }); null when idle
    let lastFrameS = 0;       // previous frame timestamp (seconds), shared by the drift + cycle accumulators

    const SKY_TRANSITION_DUR = 1.2; // seconds to glide between time-of-day presets (quick vs. the slow cycle)
    const UPLIGHT_NIGHT_LEVEL = 480; // SpotLight intensity for the night landscape uplights (also normalizes beam opacity)

    const SKY_CYCLE_PERIOD = 600; // seconds for one full sunrise→night→sunrise loop (slow, ~10 min — a calm pace)
    const SKY_CYCLE_MAX_EL = 70;  // peak sun elevation (degrees) at midday
    const DEFAULT_KEY_LIGHT_POS = new THREE.Vector3(120, 200, 80); // fixed studio key position (restored when not cycling)
    const SKY_CYCLE_LIGHT_MIN_EL = 10; // floor the shadow light's elevation so it never dips underground at night

    // `parameters` is imported from core.js so the gallery (and future modules)
    // share the same live object. It is still mutated in place throughout.

    // Initialize everything
    async function init() {
      initScene();
      setupControls();
      createLights();
      createEnvironment();
      initPineCones(); // start loading the pine-cone GLB (refreshes the tree once ready)
      syncSceneToggles(); // apply any browser-restored settings checkboxes now the scene exists
      // If opened via a share link (#t=...), rebuild that exact tree; otherwise
      // start with a fresh random one. Decoding a link is async (it may inflate a
      // compressed payload), so await it before deciding — the loading screen
      // covers the brief wait until the first render tears it down.
      if (!(await loadSharedTreeFromURL())) {
        generateNewTree();
      }
      initWinter(performance.now() * 0.001); // match the winter scene to the initial tree (no entrance animation)
      animate();
      initInteraction(); // attaches pointer listeners (hover/prune/shape)
    }

    function initScene() {
      const container = document.getElementById('world');
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Scene
      scene = new THREE.Scene();
      scene.fog = new THREE.FogExp2(0x87CEEB, 0.00078);
      app.scene = scene; // expose to feature modules (gallery, etc.)

      // Camera
      // far must clear the star dome (starfield.js domeScale, centered ~y-320) plus
      // the max orbit distance: ~(maxDistance 500 + 320) + 2600 ≈ 3420, else the far
      // plane clips the dome into a growing circle as you zoom out. 4000 leaves margin.
      camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 4000);
      camera.position.set(0, 100, 200);

      // Renderer. preserveDrawingBuffer keeps the last frame readable after it's
      // composited, so the clip recorder (recorder.js) can drawImage() the canvas
      // into its watermark compositor — and future screenshot/share-image features
      // can grab it too. Minor cost; negligible for this scene.
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance", preserveDrawingBuffer: true });
      renderer.setSize(width, height);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.56;
      renderer.localClippingEnabled = true; // for the root soil-line clip plane
      container.appendChild(renderer.domElement);

      // Enhanced color and lighting
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.physicallyCorrectLights = true;

      initSky();

      // Controls
      controls = new OrbitControls(camera, renderer.domElement);
      controls.target.set(0, 60, 0);
      controls.minPolarAngle = Math.PI * 0.05;
      controls.maxPolarAngle = Math.PI * 0.8;
      controls.minDistance = 50;
      controls.maxDistance = 500;
      controls.enableDamping = true;
      controls.dampingFactor = 0.02;

      // World-space horizontal plane at the soil surface. Root materials clip
      // against it so any root dipping below the soil (and out through the pot
      // walls/bottom) isn't drawn. Its constant is set to the tree base height
      // whenever a tree is (re)created — see createNewTree.
      app.rootClipPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

      // Expose scene-core singletons to feature modules (interaction, etc.)
      app.camera = camera;
      app.renderer = renderer;
      app.controls = controls;

      // Handle resize
      window.addEventListener('resize', onWindowResize);
    }

    function initSky() {
    sky = new Sky();
    sky.scale.setScalar(450000); // Scale the sky to be very large
    scene.add(sky);

    sun = new THREE.Vector3(); // A vector to hold the sun's position

    // Sky shader uniforms
    const uniforms = sky.material.uniforms;
    uniforms['turbidity'].value = 0.01;
    uniforms['rayleigh'].value = .52;
    uniforms['mieCoefficient'].value = 0.005;
    uniforms['mieDirectionalG'].value = 0.7;

    const phi = THREE.MathUtils.degToRad(46); // Elevation of the sun (2 degrees above horizon)
    const theta = THREE.MathUtils.degToRad(78); // Azimuth of the sun (south)

    sun.setFromSphericalCoords(1, phi, theta); // Set sun position based on phi and theta

    uniforms['sunPosition'].value.copy(sun); // Pass sun position to the sky shader

    // renderer.toneMappingExposure = 0.5;

    app.sky = sky; // expose for the cloud-drift animation in animate()

    // Night star dome. Built once, always in the scene, but starts fully
    // transparent — animate() fades it in by sun height so stars only show as the
    // sun dips toward/below the horizon (the "night" preset and the Cycle's nights).
    // fog is disabled so the far dome isn't washed out by the garden's FogExp2.
    starField = createStarField();
    starField.material.fog = false;
    scene.add(starField);
    app.starField = starField;

    // Aurora curtain on the same night dome — animate() reveals it only when it's
    // both night (the starfield's sun-height fade) and the Bare / Winter scene.
    aurora = createAurora();
    scene.add(aurora);
    app.aurora = aurora;

    // Fireflies around the tree on green-season nights (not winter, not autumn) —
    // animate() fades them with the same night factor.
    fireflies = createFireflies();
    scene.add(fireflies);
    app.fireflies = fireflies;

    // Butterflies around the tree on clear summer days — animate() shows them by the
    // sun's day factor + sky clearness (the daytime counterpart to the fireflies).
    // off until I fix the model
    // butterflies = createButterflies();
    // scene.add(butterflies);
    // app.butterflies = butterflies;
}

// Sun height as a 0..1 "day factor" derived from the sky's sun vector:
// .y is sin(elevation), so 0 ≈ sun at/below the horizon (night), 1 ≈ high day.
// Shared by the starfield fade and the shooting-star spawner.
function sunDayFactor() {
  if (!sky) return 1;
  const y = sky.material.uniforms.sunPosition.value.y; // unit vector → sin(elevation)
  return THREE.MathUtils.clamp((y + 0.15) / 0.55, 0, 1);
}

// Clouds are always drawn at the shader's default coverage; the Cloud Drift
// toggle (Settings ▸ Backdrops) only controls whether they move. Off by default
// so the sky stays calm unless the user opts into motion. The drift itself is
// accumulated in animate().
function setCloudsMoving(on) { cloudsMoving = on; }
app.setCloudsMoving = setCloudsMoving; // settings.js drives this via the checkbox

// Sky presets (Settings ▸ Backdrops ▸ Sky). Each sets the atmosphere + sun for a
// mood; the shader handles the sunset reddening automatically as the sun nears
// the horizon (low `el`). `partly` reproduces the default look. Cloud *coverage*
// is part of the preset; the Cloud Drift toggle still independently controls motion.
// The trailing fields (fog/key/keyI/ambI/fillI/exposure) are the *scene* mood —
// fog tint, key/ambient/fill light, and tone-map exposure — so the whole garden
// matches the sky, not just the dome. See applySkyMood().
const SKY_PRESETS = {
  clear:    { el: 44, az: 78,  rayleigh: 0.25, turbidity: 0.01, mie: 0.002, mieG: 0.70, reddening: 0.3, sunB: 1.00, clouds: 0.05, cloudBright: 1.00, cloudDens: 0.40, cloudElev: 0.50, skyExp: 1.00, fog: 0x9FD3EF, key: 0xfff4e0, amb: 0xffffff, keyI: 1.7, ambI: 1.15, fillI: 0.70, upI: 0.0, exposure: 1.60 },
  partly:   { el: 44, az: 78,  rayleigh: 0.52, turbidity: 0.01, mie: 0.005, mieG: 0.70, reddening: 0.4, sunB: 1.00, clouds: 0.40, cloudBright: 1.00, cloudDens: 0.40, cloudElev: 0.50, skyExp: 1.00, fog: 0x87CEEB, key: 0xfff4e0, amb: 0xffffff, keyI: 1.6, ambI: 1.10, fillI: 0.70, upI: 0.0, exposure: 1.56 },
  overcast: { el: 34, az: 78,  rayleigh: 3.80, turbidity: 15.00, mie: 0.15, mieG: 0.15, reddening: 0.0, sunB: 0.00, clouds: 0.55, cloudBright: 0.45, cloudDens: 0.60, cloudElev: 0.40, skyExp: 0.2, fog: 0x9BA1A6, key: 0xAEB3B8, amb: 0xC2C8CE, keyI: 0.9, ambI: 1.05, fillI: 0.55, upI: 0.0, exposure: 1.30 },
  golden:   { el: 2.45,  az: 78,  rayleigh: 1.60, turbidity: 3.00, mie: 0.006, mieG: 0.65, reddening: 1.0, sunB: 0.16, clouds: 0.40, cloudBright: 1.00, cloudDens: 0.40, cloudElev: 0.50, skyExp: 1.00, fog: 0xE8A87C, key: 0xFF7A2E, amb: 0xFFE8D5, keyI: 1.3, ambI: 0.85, fillI: 0.50, upI: 0.0, exposure: 1.50 },
  dawn:     { el: 4.5,  az: 250, rayleigh: 1.60, turbidity: 3.00, mie: 0.002, mieG: 0.60, reddening: 0.9, sunB: 0.22, clouds: 0.20, cloudBright: 1.00, cloudDens: 0.40, cloudElev: 0.50, skyExp: 1.00, fog: 0xD9A6B0, key: 0xFFB07A, amb: 0xFFE0E6, keyI: 1.1, ambI: 0.80, fillI: 0.60, upI: 0.0, exposure: 1.45 },
  night:    { el: -8, az: 78,  rayleigh: 0.40, turbidity: 0.01, mie: 0.003, mieG: 0.70, reddening: 0.0, sunB: 0.00, clouds: 0.12, cloudBright: 1.00, cloudDens: 0.40, cloudElev: 0.50, skyExp: 1.00, fog: 0x0B1026, key: 0x3A4A7A, amb: 0xa2c4f3, keyI: 0.2, ambI: 0.40, fillI: 0.35, upI: UPLIGHT_NIGHT_LEVEL, exposure: 1.05 },
};

// Day → golden-horizon → night scene palettes, blended by sun elevation for the
// "Cycle" preset (static presets carry their own mood inline above). Reusable
// THREE.Color instances + one scratch mood object keep the per-frame blend
// allocation-free.
const MOOD_DAY    = { fog: new THREE.Color(0x87CEEB), key: new THREE.Color(0xfff4e0), amb: new THREE.Color(0xffffff), keyI: 1.60, ambI: 1.10, fillI: 0.70, upI: 0.0, exposure: 1.56 };
const MOOD_GOLDEN = { fog: new THREE.Color(0xE8A87C), key: new THREE.Color(0xFF8C42), amb: new THREE.Color(0xFFE8D5), keyI: 1.30, ambI: 0.80, fillI: 0.50, upI: 0.0, exposure: 1.42 };
const MOOD_NIGHT  = { fog: new THREE.Color(0x0B1026), key: new THREE.Color(0x3A4A7A), amb: new THREE.Color(0xa2c4f3), keyI: 0.18, ambI: 0.38, fillI: 0.35, upI: UPLIGHT_NIGHT_LEVEL, exposure: 1.05 };
const _moodScratch = { fog: new THREE.Color(), key: new THREE.Color(), amb: new THREE.Color(), keyI: 0, ambI: 0, fillI: 0, upI: 0, exposure: 0 };

function lerpMood(out, a, b, t) {
  out.fog.lerpColors(a.fog, b.fog, t);
  out.key.lerpColors(a.key, b.key, t);
  out.amb.lerpColors(a.amb, b.amb, t);
  out.keyI = a.keyI + (b.keyI - a.keyI) * t;
  out.ambI = a.ambI + (b.ambI - a.ambI) * t;
  out.fillI = a.fillI + (b.fillI - a.fillI) * t;
  out.upI = a.upI + (b.upI - a.upI) * t;
  out.exposure = a.exposure + (b.exposure - a.exposure) * t;
  return out;
}

// Pick the scene mood for a given sun elevation (degrees): full day above ~20°,
// warm golden hour through the low sun, deep night once the sun is well under.
function moodForElevation(el) {
  if (el >= 20) return MOOD_DAY;
  if (el >= 3)  return lerpMood(_moodScratch, MOOD_GOLDEN, MOOD_DAY, (el - 3) / 17);
  if (el >= -6) return lerpMood(_moodScratch, MOOD_NIGHT, MOOD_GOLDEN, (el + 6) / 9);
  return MOOD_NIGHT;
}

// Drive the night uplights: set both SpotLights' intensity, show/hide the gravel
// fixtures (housing + lens), and fade the faux beam cones in proportionally
// (0 by day → full at UPLIGHT_NIGHT_LEVEL). Shared by the cycle mood and the preset
// tween so lights + fixtures + beams always move together.
function setUplightLevel(upI) {
  if (app.upLight1) app.upLight1.intensity = upI;
  if (app.upLight2) app.upLight2.intensity = upI;
  const on = upI > 1;
  const k = THREE.MathUtils.clamp(upI / UPLIGHT_NIGHT_LEVEL, 0, 1);
  if (app.upFixtures) for (const f of app.upFixtures) f.visible = on;
  if (app.upBeams) for (const b of app.upBeams) { b.visible = on; b.material.uniforms.uOpacity.value = 0.16 * k; }
  setLanternNight(k); // fade the stone lanterns' fireboxes in on the same dusk/cycle
}

// Push a mood onto the scene: fog tint, key/ambient/fill light, tone-map exposure.
// Accepts both inline preset hex values and the THREE.Color cycle palettes (Color
// .set() and .copy()-free assignment handle either).
function applySkyMood(m) {
  if (app.scene && app.scene.fog) app.scene.fog.color.set(m.fog);
  if (app.keyLight)     { app.keyLight.color.set(m.key); app.keyLight.intensity = m.keyI; }
  if (app.ambientLight) { app.ambientLight.color.set(m.amb); app.ambientLight.intensity = m.ambI; }
  if (app.fillLight)    app.fillLight.intensity = m.fillI;
  setUplightLevel(m.upI);
  if (app.renderer)     app.renderer.toneMappingExposure = m.exposure;
}

// Position the sun from an elevation above the horizon + a compass azimuth.
// (three's spherical phi is measured from straight up, so phi = 90° − elevation.)
function setSunElevation(elevationDeg, azimuthDeg) {
  if (!sky) return;
  const phi = THREE.MathUtils.degToRad(90 - elevationDeg);
  const theta = THREE.MathUtils.degToRad(azimuthDeg);
  sun.setFromSphericalCoords(1, phi, theta);
  sky.material.uniforms.sunPosition.value.copy(sun);
}

// A full sky "state": every value a static preset controls — atmosphere uniforms,
// sun direction, scene mood, and the shadow-light position. Picking a preset tweens
// from the live state to the target one (see animate()), so time-of-day changes
// glide instead of snapping. The unit sun vector lerps through the horizon, so a
// day→night pick naturally reddens as it crosses dusk.
function sunVectorFor(elevationDeg, azimuthDeg) {
  const phi = THREE.MathUtils.degToRad(90 - elevationDeg);
  const theta = THREE.MathUtils.degToRad(azimuthDeg);
  return new THREE.Vector3().setFromSphericalCoords(1, phi, theta);
}

// Where to place the shadow-casting key light for a given sun elevation/azimuth:
// along the sun's direction (so shadows point away from it) at a fixed distance,
// with elevation floored so the light never dips underground. Low sun → long
// shadows (golden hour / dawn). Used by both the static presets and the cycle.
function keyLightPosFor(elevationDeg, azimuthDeg) {
  const el = Math.max(elevationDeg, SKY_CYCLE_LIGHT_MIN_EL);
  return new THREE.Vector3().setFromSphericalCoords(
    250, THREE.MathUtils.degToRad(90 - el), THREE.MathUtils.degToRad(azimuthDeg)
  );
}

function captureSkyState() {
  const u = sky.material.uniforms;
  return {
    rayleigh: u.rayleigh.value, turbidity: u.turbidity.value, mie: u.mieCoefficient.value,
    mieG: u.mieDirectionalG.value, reddening: u.sunsetReddening.value, sunB: u.sunBrightness.value,
    clouds: u.cloudCoverage.value, cloudBright: u.cloudBright.value,
    cloudDens: u.cloudDensity.value, cloudElev: u.cloudElevation.value, skyExp: u.skyExposure.value,
    keyI: app.keyLight ? app.keyLight.intensity : 1.6,
    ambI: app.ambientLight ? app.ambientLight.intensity : 1.1,
    fillI: app.fillLight ? app.fillLight.intensity : 0.7,
    upI: app.upLight1 ? app.upLight1.intensity : 0.0,
    exposure: app.renderer ? app.renderer.toneMappingExposure : 1.56,
    fog: (app.scene && app.scene.fog) ? app.scene.fog.color.clone() : new THREE.Color(0x87CEEB),
    key: app.keyLight ? app.keyLight.color.clone() : new THREE.Color(0xfff4e0),
    amb: app.ambientLight ? app.ambientLight.color.clone() : new THREE.Color(0xffffff),
    sun: u.sunPosition.value.clone().normalize(),
    lightPos: app.keyLight ? app.keyLight.position.clone() : DEFAULT_KEY_LIGHT_POS.clone(),
  };
}

function skyStateFromPreset(p) {
  return {
    rayleigh: p.rayleigh, turbidity: p.turbidity, mie: p.mie, mieG: p.mieG,
    reddening: p.reddening, sunB: p.sunB, clouds: p.clouds, cloudBright: p.cloudBright,
    cloudDens: p.cloudDens, cloudElev: p.cloudElev, skyExp: p.skyExp,
    keyI: p.keyI, ambI: p.ambI, fillI: p.fillI, upI: p.upI, exposure: p.exposure,
    fog: new THREE.Color(p.fog), key: new THREE.Color(p.key), amb: new THREE.Color(p.amb ?? 0xffffff),
    sun: sunVectorFor(p.el, p.az),
    lightPos: keyLightPosFor(p.el, p.az), // key light follows the sun → long shadows at low-sun presets
  };
}

function applySkyState(s) {
  const u = sky.material.uniforms;
  u.rayleigh.value = s.rayleigh; u.turbidity.value = s.turbidity; u.mieCoefficient.value = s.mie;
  u.mieDirectionalG.value = s.mieG; u.sunsetReddening.value = s.reddening; u.sunBrightness.value = s.sunB;
  u.cloudCoverage.value = s.clouds; u.cloudBright.value = s.cloudBright;
  u.cloudDensity.value = s.cloudDens; u.cloudElevation.value = s.cloudElev; u.skyExposure.value = s.skyExp;
  u.sunPosition.value.copy(s.sun);
  if (app.scene && app.scene.fog) app.scene.fog.color.copy(s.fog);
  if (app.keyLight) { app.keyLight.color.copy(s.key); app.keyLight.intensity = s.keyI; app.keyLight.position.copy(s.lightPos); }
  if (app.ambientLight) { app.ambientLight.color.copy(s.amb); app.ambientLight.intensity = s.ambI; }
  if (app.fillLight) app.fillLight.intensity = s.fillI;
  setUplightLevel(s.upI);
  if (app.renderer) app.renderer.toneMappingExposure = s.exposure;
}

// Reused scratch so the per-frame tween allocates nothing.
const _skyStateScratch = { fog: new THREE.Color(), key: new THREE.Color(), amb: new THREE.Color(), sun: new THREE.Vector3(), lightPos: new THREE.Vector3() };

function lerpSkyState(out, a, b, t) {
  const L = (x, y) => x + (y - x) * t;
  out.rayleigh = L(a.rayleigh, b.rayleigh); out.turbidity = L(a.turbidity, b.turbidity); out.mie = L(a.mie, b.mie);
  out.mieG = L(a.mieG, b.mieG); out.reddening = L(a.reddening, b.reddening); out.sunB = L(a.sunB, b.sunB);
  out.clouds = L(a.clouds, b.clouds); out.cloudBright = L(a.cloudBright, b.cloudBright);
  out.cloudDens = L(a.cloudDens, b.cloudDens); out.cloudElev = L(a.cloudElev, b.cloudElev); out.skyExp = L(a.skyExp, b.skyExp);
  out.keyI = L(a.keyI, b.keyI); out.ambI = L(a.ambI, b.ambI);
  out.fillI = L(a.fillI, b.fillI); out.upI = L(a.upI, b.upI); out.exposure = L(a.exposure, b.exposure);
  out.fog.lerpColors(a.fog, b.fog, t); out.key.lerpColors(a.key, b.key, t); out.amb.lerpColors(a.amb, b.amb, t);
  out.sun.lerpVectors(a.sun, b.sun, t).normalize();
  out.lightPos.lerpVectors(a.lightPos, b.lightPos, t);
  return out;
}

// Apply a named sky preset. "cycle" hands the sun over to animate(); every other
// name tweens there over SKY_TRANSITION_DUR — or snaps when `instant` (used at boot
// so a restored preset doesn't visibly animate in). settings.js drives this.
function applySkyPreset(name, instant = false) {
  if (!sky) return;
  if (name === 'cycle') {
    skyTransition = null; // cycle owns the sun from here
    skyCycle = true;
    cycleTime = 0; // start the loop at sunrise (elevation 0)
    sky.material.uniforms.sunsetReddening.value = 1.0; // let dawn/dusk redden as the sun crosses the horizon
    // restore default clouds + dome brightness (in case we came from overcast)
    sky.material.uniforms.cloudBright.value = 1.0;
    sky.material.uniforms.cloudDensity.value = 0.4;
    sky.material.uniforms.cloudElevation.value = 0.5;
    sky.material.uniforms.skyExposure.value = 1.0;
    return;
  }
  skyCycle = false;
  const target = skyStateFromPreset(SKY_PRESETS[name] || SKY_PRESETS.partly);
  if (instant) { applySkyState(target); skyTransition = null; return; }
  skyTransition = { t: 0, from: captureSkyState(), to: target }; // tweened in animate()
}
app.applySkyPreset = applySkyPreset; // settings.js drives this from the #skyPreset dropdown


window.mainBranches = function() {
  if (tree) {
    // Sanity cap: main branches each haul their own sub-branches/twigs/foliage,
    // so they drive the cost. Block before adding once we're at the limit. This
    // only counts the live scene graph — it never touches the seeded stream, so
    // save replay is unaffected (and pre-cap saves still load as authored).
    if (tree.mainBranchCount() >= MAX_MAIN_BRANCHES) {
      notify(`🌳 Max ${MAX_MAIN_BRANCHES} main branches reached`, 'linear-gradient(45deg, #c0392b, #e67e22)');
      return;
    }
    // Branch count + placement now come from the seeded RNG inside the Tree, so
    // the action is reproducible. Evaluate the count first (one stream draw),
    // then add — replay (replayHistory) does the same in the same order.
    tree.beginRecordedAction(); // seed this action's slot so it replays identically
    const count = tree.mainBranchBatchSize();
    // New growth arrives bare; snow settles on it over time (no-op outside winter).
    setSnowStartsBare(true);
    try { tree.addMainBranches(count, true); } finally { setSnowStartsBare(false); }
    tree.record({ op: 'mainBranches' });
    playSfx('growBig'); // a bigger growth burst for the main branches
  }
};


    function createLights() {
      // Ambient light (sky/ground fill — raised to lift the overall scene)
      const ambientLight = new THREE.HemisphereLight(0xffffff, 0x666655, 1.1);
      scene.add(ambientLight);

      // Main directional light (key)
      const directionalLight = new THREE.DirectionalLight(0xfff4e0, 1.6);
      directionalLight.position.set(120, 200, 80);
      // Expose the key + ambient (and fill, below) so sky presets can retint and
      // dim them to match the sky — see applySkyMood().
      app.keyLight = directionalLight;
      app.ambientLight = ambientLight;
      directionalLight.castShadow = true;
      // 4096² (not 2048) so the wide frustum below stays crisp: ±800 over 4096
      // gives the same texel density as ±400 over 2048 would.
      directionalLight.shadow.mapSize.width = 4096;
      directionalLight.shadow.mapSize.height = 4096;
      directionalLight.shadow.camera.near = 1;
      directionalLight.shadow.camera.far = 1800;
      // Wide enough to enclose every placed decor item AND the shadow each one
      // throws — not just the centered bonsai: lantern (x=288), short lantern
      // (-210/210), and the distant torii gate (z=700). The box runs to ±1000 so
      // the torii's shadow has room past the gate instead of clipping at the edge;
      // the 4096² map above keeps shadows sharp despite the larger area.
      directionalLight.shadow.camera.left = -1000;
      directionalLight.shadow.camera.right = 1000;
      directionalLight.shadow.camera.top = 1000;
      directionalLight.shadow.camera.bottom = -1000;
      scene.add(directionalLight);

      // Cool blue tone from opposite side for balanced illumination
      const fillLight = new THREE.DirectionalLight(0xB8D4F0, 0.7);
      fillLight.position.set(-80, 120, -60);
      app.fillLight = fillLight;
      scene.add(fillLight);

      // Night landscape uplights: two SpotLights sitting just above the gravel,
      // aimed UP at the bonsai so they throw obvious cones into the canopy (like
      // staked garden uplights). A small emissive fixture marks each source on the
      // gravel. Off by day — the sky mood fades them in at night via setUplightLevel
      // (driven by each preset's `upI`). Warm + cool for a two-tone wash.
      // The gravel surface sits at y = -29 (see environment.js). Seat the lights
      // and their fixtures right on it so the sources don't float.
      const SAND_Y = -29;
      const BEAM_ANGLE = 0.5; // spotlight cone half-angle; the faux beam matches it
      const upTarget = new THREE.Object3D();
      upTarget.position.set(0, 75, 0); // aim at mid-canopy
      scene.add(upTarget);

      app.upFixtures = []; // black housings + bright lenses (toggled on at night)
      app.upBeams = [];    // faux volumetric cones (toggled + opacity-driven at night)

      const fixtureGeo = new THREE.CylinderGeometry(2.6, 3.6, 2.4, 18);
      const lensGeo = new THREE.CylinderGeometry(2.0, 2.0, 0.6, 18);
      const ballGeo = new THREE.SphereGeometry(2.7, 16, 12); // pivot ball the housing sits on

      // A soft additive cone, apex at the fixture, fading to nothing toward the
      // tree — a cheap volumetric beam. Alpha ramps down along the cone's length.
      const makeBeam = (colorHex, from, to) => {
        const dir = new THREE.Vector3().subVectors(to, from);
        const len = dir.length();
        // Truncated cone: a non-zero mouth at the fixture (so it doesn't pinch to a
        // point) widening to the spotlight's cone radius at the tree.
        const geo = new THREE.CylinderGeometry(2.50, Math.tan(BEAM_ANGLE) * len, len, 28, 1, true);
        geo.translate(0, -len / 2, 0); // narrow mouth at local origin (fixture), wide base at (0, -len, 0)
        const mat = new THREE.ShaderMaterial({
          uniforms: { uColor: { value: new THREE.Color(colorHex) }, uOpacity: { value: 0.0 }, uLen: { value: len } },
          vertexShader: 'varying float vY; void main(){ vY = position.y; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
          fragmentShader: 'uniform vec3 uColor; uniform float uOpacity; uniform float uLen; varying float vY;' +
            'void main(){ float a = clamp(1.0 + vY / uLen, 0.0, 1.0); a = pow(a, 1.6); gl_FragColor = vec4(uColor, a * uOpacity); }',
          transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.copy(from);
        mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize().negate());
        mesh.raycast = () => {}; // pure decor: never a prune/shape pick (raycast ignores .visible)
        return mesh;
      };

      // SpotLight(color, intensity, distance, angle, penumbra, decay) just above the
      // gravel, plus its black housing, a bright lens (the glowing "bulb" — fine to
      // blow to white), and the faux beam. Intensity starts at 0; the night mood
      // drives the spot + beam opacity + visibility via setUplightLevel.
      const makeUplight = (color, lensColor, x, z) => {
        // Aim direction: from the fixture's spot on the gravel up to the canopy.
        // The housing, lens, and spot all tilt to face it (a staked uplight angled
        // at the tree) instead of standing straight up.
        const basePos = new THREE.Vector3(x, SAND_Y + 1.2, z); // housing center, on the gravel
        const aim = new THREE.Vector3().subVectors(upTarget.position, basePos).normalize();
        const tilt = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), aim);

        const s = new THREE.SpotLight(color, 0, 480, BEAM_ANGLE, 0.5, 1.0);
        s.position.copy(basePos).addScaledVector(aim, 1.8); // emit from the fixture mouth
        s.target = upTarget;
        scene.add(s);

        const housing = new THREE.Mesh(fixtureGeo, new THREE.MeshBasicMaterial({ color: 0x000000 }));
        housing.position.copy(basePos);
        housing.quaternion.copy(tilt); // point along the beam, not straight up
        housing.visible = false;
        scene.add(housing);

        // Pivot ball at the back (bottom) of the housing — the fixture looks like it
        // sits on the ball and is tilted on it. Rests on the gravel.
        const ball = new THREE.Mesh(ballGeo, new THREE.MeshBasicMaterial({ color: 0x000000 }));
        ball.position.copy(basePos).addScaledVector(aim, -1.1);
        ball.visible = false;
        scene.add(ball);

        const lens = new THREE.Mesh(lensGeo, new THREE.MeshBasicMaterial({ color: lensColor }));
        lens.position.copy(basePos).addScaledVector(aim, 1.3); // seated in the tilted housing mouth
        lens.quaternion.copy(tilt);
        lens.visible = false;
        scene.add(lens);

        const beam = makeBeam(color, basePos.clone().addScaledVector(aim, 1.5), upTarget.position);
        beam.visible = false;
        scene.add(beam);

        // Decor only — keep these out of the prune/shape raycast (which only
        // inspects the frontmost hit, and which ignores .visible). Otherwise the
        // housing/lens/ball can occlude branches and silently swallow clicks.
        housing.raycast = ball.raycast = lens.raycast = () => {};

        app.upFixtures.push(housing, lens, ball);
        app.upBeams.push(beam);
        return s;
      };
      app.upLight1 = makeUplight(0xFFD9A0, 0xFFE9C8, -46, 38);  // warm, front-left
      app.upLight2 = makeUplight(0xBFD6FF, 0xE3EEFF, 50, -40);   // cool, back-right

      // Rim light
      const rimLight = new THREE.DirectionalLight(0xffffff, 0.5);
      rimLight.position.set(-50, 50, -100);
      scene.add(rimLight);

      // Simulates light bouncing off the ground
      const bounceLight = new THREE.DirectionalLight(0x8B7355, 0.8);
      bounceLight.position.set(0, -50, 0);
      bounceLight.target.position.set(0, 50, 0);
      scene.add(bounceLight);
      scene.add(bounceLight.target);

      // Add warmth and depth to the scene
      const warmLight1 = new THREE.PointLight(0xFFB347, 1.8, 200);
      warmLight1.position.set(60, 40, 60);
      scene.add(warmLight1);

      const warmLight2 = new THREE.PointLight(0xFF8C69, 1.6, 150);
      warmLight2.position.set(-40, 30, -40);
      scene.add(warmLight2);
    }

    // Enhanced tree class with better branch attachment


    // Event handlers
    function onWindowResize() {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    }

    // Global functions for buttons
    window.generateNewTree = function() {
      playSfx('pullOut'); // old tree being pulled out, the moment the button is hit
      if (tree) {
        gsap.to(tree.mesh.position, {
          y: 200,
          duration: 1,
          ease: "power2.in",
          onComplete: () => {
            scene.remove(tree.mesh);
            createNewTree();
          }
        });
      } else {
        createNewTree();
      }
    };

    function createNewTree() {
      tree = new Tree();
      app.tree = tree;
      // A fresh tree isn't tied to any saved entry — reset the name/id so Save
      // creates a new gallery entry rather than overwriting a loaded one.
      app.newTreeIdentity?.();
      tree.mesh.position.y = 8.5;
      // Clip roots at the soil surface (= the tree base in world space).
      app.rootClipPlane.constant = -tree.mesh.position.y;
      scene.add(tree.mesh);
      tree.prewarmDebris?.(); // compile prune-offcut shaders now so the first cut is smooth

      // Animate tree growing
      tree.mesh.scale.set(0.01, 0.01, 0.01);
      playSfx('growBig', { rate: 0.8 }); // the whole tree sprouting — a slow, full grow
      gsap.to(tree.mesh.scale, {
        x: 1, y: 1, z: 1,
        duration: 2,
        ease: "back.out(1.7)"
      });
    }

    // Rebuild a tree from saved gallery data. Lives here (not in gallery.js) so
    // tree creation and the `tree`/scene singletons stay owned by one module;
    // gallery invokes it through app.createNewTreeWithData.
    function createNewTreeWithData(treeData) {
      if (tree) {
        scene.remove(tree.mesh);
      }

      // Reproduce the exact tree: same seed regenerates the identical base
      // structure (trunk/branches/roots/foliage colors), then the recorded edit
      // log replays the post-generation grow/prune/shape actions in order.
      // `parameters` were already applied by gallery.loadTree before this call.
      // Legacy saves without a seed fall back to a fresh random tree.

      // Branches are sized from the trunk dims at the moment they're created and
      // are NOT resized when the trunk is later reshaped (on purpose). So if the
      // saved tree's trunk was dragged after generation, build the base at the
      // ORIGINAL (genTrunc) dims so its branches come out the right size, let
      // replay restore each later grow's own dims, then snap the trunk back to the
      // final/display dims below — re-attaching branches without resizing, exactly
      // as the live session did. `truncHeight`/`truncThickness` are all branch size
      // depends on; curve/noise (and everything else) ride along in `parameters`.
      const finalH = parameters.truncHeight, finalT = parameters.truncThickness;
      if (treeData.genTrunc) {
        parameters.truncHeight = treeData.genTrunc.h;
        parameters.truncThickness = treeData.genTrunc.t;
      }

      tree = new Tree(treeData.seed);
      app.tree = tree;

      // Apply lean data if available (redundant once seeded — the lean is rolled
      // from the seed — but kept for legacy/seedless saves).
      if (treeData.leanData) {
        tree.leanX = treeData.leanData.leanX;
        tree.leanZ = treeData.leanData.leanZ;
        tree.curveStrength = treeData.leanData.curveStrength;
      }

      // Replay recorded edits onto the freshly seeded tree (may reshape the trunk
      // mid-replay to size each grown batch against the trunk it was grown on).
      tree.replayHistory(treeData.history);

      // Restore the final/display trunk if the build or replay left it elsewhere,
      // reshaping (re-attach, no resize) to mirror the session's end state.
      if (parameters.truncHeight !== finalH || parameters.truncThickness !== finalT) {
        parameters.truncHeight = finalH;
        parameters.truncThickness = finalT;
        tree.updateTrunkGeometry();
      }

      // Sit the loaded tree on the soil surface, same as a freshly generated
      // one — otherwise its base (and roots) sink below the soil and vanish.
      tree.mesh.position.y = 8.5;
      app.rootClipPlane.constant = -tree.mesh.position.y;
      scene.add(tree.mesh);
      tree.prewarmDebris?.(); // compile prune-offcut shaders now so the first cut is smooth

      // Animate tree loading
      tree.mesh.scale.set(0.01, 0.01, 0.01);
      gsap.to(tree.mesh.scale, {
        x: 1, y: 1, z: 1,
        duration: 1.5,
        ease: "back.out(1.7)"
      });
    }
    app.createNewTreeWithData = createNewTreeWithData;

    window.growMoreBranches = function() {
  if (tree) {
    // Sanity cap on the finer growth (sub-branches + twigs have no per-action
    // ceiling). Block before adding once we're at the limit. Counts the live
    // scene graph only — never the seeded stream — so save replay is unaffected.
    if (tree.branchCount() >= MAX_GROW_BRANCHES) {
      notify(`🌿 Max ${MAX_GROW_BRANCHES} branches reached`, 'linear-gradient(45deg, #c0392b, #e67e22)');
      return;
    }
    // 50% chance to add trunk branches, 50% chance to add main branch sub-branches.
    // The coin flip stays on Math.random (we record the RESULT, so it needn't be
    // seeded); the chosen method's geometry is seeded and so reproducible.
    const kind = Math.random() < 0.5 ? 'addBranches' : 'addSubBranches';
    tree.beginRecordedAction(); // seed this action's slot so it replays identically
    // New growth arrives bare; snow settles on it over time (no-op outside winter).
    setSnowStartsBare(true);
    try {
      if (kind === 'addBranches') {
        tree.addMoreBranches();
      } else {
        tree.addMoreSubBranches();
      }
      // Also bush out existing branches with a few new twigs each grow.
      tree.growMoreTwigs();
    } finally {
      setSnowStartsBare(false);
    }
    tree.record({ op: 'grow', kind });
    playSfx('grow');
  }
};

    // Loading-screen dismissal: fade it out once the scene has actually
    // rendered a frame, but keep it up for a brief minimum so it doesn't flash.
    const LOADER_MIN_MS = 900;
    const loaderStart = performance.now();
    let loaderDismissed = false;
    function dismissLoader() {
      if (loaderDismissed) return;
      loaderDismissed = true;
      const el = document.getElementById('loading-screen');
      if (!el) return;
      const wait = Math.max(0, LOADER_MIN_MS - (performance.now() - loaderStart));
      setTimeout(() => {
        el.classList.add('is-hidden');
        el.addEventListener('transitionend', () => el.remove(), { once: true });
        playIntroCamera(); // dolly + zoom-in as the loader fades to reveal the scene
      }, wait);
    }

    // One-shot entrance move: start pushed back with a wider lens, then ease into
    // the resting view (the position/fov set in initScene) over ~0.5s. controls.update()
    // re-reads camera.position each frame, so tweening it directly is respected;
    // orbit input is parked for the half-second so the user can't fight the move.
    const INTRO_DUR = 3.64; // seconds — entrance dolly + zoom-in
    function playIntroCamera() {
      if (!camera || !controls || typeof gsap === 'undefined') return;
      const target = controls.target;
      const endPos = camera.position.clone();
      const endFov = camera.fov;
      // Pull back along the view ray and widen the lens for the starting frame.
      camera.position.copy(target).addScaledVector(endPos.clone().sub(target), 1.68);
      camera.fov = 68;
      camera.updateProjectionMatrix();
      controls.enabled = false;
      gsap.to(camera.position, { x: endPos.x, y: endPos.y, z: endPos.z, duration: INTRO_DUR, ease: 'power2.out' });
      gsap.to(camera, {
        fov: endFov, duration: INTRO_DUR, ease: 'power2.out',
        onUpdate: () => camera.updateProjectionMatrix(),
        onComplete: () => { controls.enabled = true; },
      });
    }

    function animate() {
      requestAnimationFrame(animate);

      // Drive the foliage wind sway: one shared uniform set ticks every needle
      // and leaf material across the whole tree (see updateFoliageWind in tree.js).
      // Use performance.now() (ms since page load), NOT Date.now(): the epoch time
      // is ~1.75e9, and as a 32-bit shader float its per-frame change rounds away,
      // freezing the sway. performance.now() stays small enough to keep precision.
      updateFoliageWind(performance.now() * 0.001);

      // Autumn foliage periodically sheds a leaf that flutters to the ground.
      updateLeafFall(performance.now() * 0.001, tree);

      // Bare / Winter mode: drive the falling snow and toggle the garden frost.
      updateWinter(performance.now() * 0.001);

      // Autumn palette: dry the distant grass to brown (idempotent no-op otherwise).
      setEnvironmentAutumn(parameters.foliageColor === 'autumn');

      // Sky animations share one frame delta. Accumulators advance only while
      // their feature is active (and the stamp always advances), so toggling a
      // feature on resumes from where it froze instead of jumping forward.
      const nowS = performance.now() * 0.001;
      const dt = nowS - lastFrameS;
      lastFrameS = nowS;

      // Wild deer grazing/roaming the far meadow (no-op until a ring backdrop is up).
      updateDeer(dt);

      // Shiba pup at the house: sits on the deck, then roams (no-op until the house is up).
      updateShiba(dt);

      // Cloud Drift toggle: animate the always-present clouds.
      if (cloudsMoving && sky) {
        cloudTime += dt;
        sky.material.uniforms.time.value = cloudTime;
      }

      // Sky preset "Cycle": sweep the sun in a slow arc — rising in the east,
      // overhead at midday, setting in the west, then below the horizon for night.
      if (skyCycle && sky) {
        cycleTime += dt;
        const dayAngle = (cycleTime / SKY_CYCLE_PERIOD) * Math.PI * 2;
        const elevation = SKY_CYCLE_MAX_EL * Math.sin(dayAngle);
        const azimuthDeg = THREE.MathUtils.radToDeg(dayAngle) + 78;
        setSunElevation(elevation, azimuthDeg);
        // Dim the disc as it nears/dips below the horizon so dawn/dusk redden
        // instead of blowing out — full brightness only once well up (≥20°).
        sky.material.uniforms.sunBrightness.value = THREE.MathUtils.clamp(0.12 + elevation / 20, 0.12, 1.0);
        applySkyMood(moodForElevation(elevation)); // garden tracks the moving sun
        // Cast the key light (and its shadows) from the sun's direction so shadows
        // sweep and lengthen with it (floored above the horizon by keyLightPosFor).
        if (app.keyLight) app.keyLight.position.copy(keyLightPosFor(elevation, azimuthDeg));
      }

      // Time-of-day preset change: glide from the prior sky to the picked one over
      // a short tween (mutually exclusive with Cycle — picking a static preset
      // clears skyCycle, picking Cycle clears the tween).
      if (skyTransition && sky) {
        skyTransition.t = Math.min(1, skyTransition.t + dt / SKY_TRANSITION_DUR);
        const k = skyTransition.t * skyTransition.t * (3 - 2 * skyTransition.t); // smoothstep ease
        applySkyState(lerpSkyState(_skyStateScratch, skyTransition.from, skyTransition.to, k));
        if (skyTransition.t >= 1) skyTransition = null;
      }

      // Stars: fade the dome in as the sun sinks (full once it's at/below the
      // horizon) and let shooting stars streak only at night. One sun-height
      // factor drives both, so this tracks the night preset and the Cycle alike.
      const dayFactor = sunDayFactor();
      const nightVis = 1 - THREE.MathUtils.smoothstep(dayFactor, 0.12, 0.42);
      if (starField) {
        starField.material.opacity = nightVis * 0.9; // colors are pre-brightened; cap the additive bloom
        starField.visible = nightVis > 0.001;
      }
      updateShootingStars(scene, dt, dayFactor);

      // Night ambient effects share the starfield's night fade (nightVis); their
      // season gates are eased inside each updater so they glide in/out.
      const isWinter = parameters.leafType === 'none' || parameters.leafType === 'evergreen';
      const isAutumn = parameters.foliageColor === 'autumn';
      // Aurora: winter nights only (Bare / Winter scene).
      updateAurora(nowS, dt, nightVis, isWinter);
      // Fireflies: green-season nights only — skip winter (bare) and autumn.
      updateFireflies(nowS, dt, nightVis, !isWinter && !isAutumn);
      // Butterflies: clear summer days only — skip winter, autumn and spring (cherry
      // blossom or the Spring-Green palette), and fade with the sun + sky clearness.
      const isSpring = parameters.leafType === 'cherry' || parameters.foliageColor === 'spring';
      const cloudCover = sky ? sky.material.uniforms.cloudCoverage.value : 0;
      const clearFactor = 1 - THREE.MathUtils.smoothstep(cloudCover, 0.42, 0.55);
      updateButterflies(nowS, dt, dayFactor, clearFactor, !isWinter && !isAutumn && !isSpring);

      // Ambient wildlife: birds by day, crickets at night, silent under overcast.
      // Drive it off the live sun vector (so it tracks both static presets and the
      // Cycle); overcast is the only preset that drops the dome exposure below 1.
      if (sky) {
        const y = THREE.MathUtils.clamp(sky.material.uniforms.sunPosition.value.y, -1, 1);
        const elevationDeg = THREE.MathUtils.radToDeg(Math.asin(y));
        const overcast = sky.material.uniforms.skyExposure.value < 0.5;
        updateAmbient(elevationDeg, overcast);
      }

      controls.update();
      renderer.render(scene, camera);
      dismissLoader(); // first successful render tears down the loading screen

    }

    // Initialize the game
    init();